import { useEffect, useState } from "react";
import { BadgeCheck, Calculator, Package, Search, Truck } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import RateCalculator from "../components/RateCalculator";
import AiCta from "../components/ai/AiCta";
import { usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";

const PERKS = [
  { icon: Truck, title: "Multiple Couriers", desc: "Compare services across our courier partner network." },
  { icon: BadgeCheck, title: "Inclusive of GST", desc: "Displayed prices include 18% GST — no surprises." },
  { icon: Package, title: "Free Pickup", desc: "Book and schedule doorstep pickup after your quote." },
  { icon: Search, title: "Track Anytime", desc: "Every booking comes with live shipment tracking." },
];

export default function RateCalculatorPage() {
  const [contact, setContact] = useState(null);
  const [searchParams] = useSearchParams();
  usePageMeta(
    "Courier Rate Calculator — Domestic & International | IndiaFast",
    "Compare courier rates instantly for domestic and international shipments — live partner rates, chargeable weight and GST-inclusive pricing.",
    "/rate-calculator"
  );
  useEffect(() => { api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rate-calculator-page">
      <Navbar />

      <section className="relative overflow-hidden bg-navy pb-16 pt-32 sm:pt-36" data-testid="rate-hero">
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-saffron/15 blur-3xl" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-saffron">
            <Calculator className="h-4 w-4" /> Pricing
          </p>
          <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Courier Rate Calculator
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Compare live domestic and international courier rates instantly. Enter your pickup and delivery details to see prices across our courier partner network.
          </p>
          <div className="mt-5"><AiCta label="Ask IndiaFast Assistant" action="start:rate" testid="rate-ask-ai-btn" /></div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-16" data-testid="rate-main">
        <div id="rates" className="scroll-mt-24 rounded-3xl border border-slate-200 bg-white p-5 shadow-xl sm:p-8">
          <RateCalculator
            initialMode={searchParams.get("mode") === "international" ? "international" : "domestic"}
            initialCountry={searchParams.get("country") || ""}
            initialWeight={searchParams.get("weight") || ""}
          />
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PERKS.map((p, i) => (
            <div key={i} data-testid={`rate-perk-${i}`}
                 className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-saffron/10 text-saffron"><p.icon className="h-5 w-5" /></span>
              <p className="mt-3 font-heading text-sm font-bold text-navy">{p.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-2xl bg-navy p-6 sm:flex-row sm:p-8" data-testid="rate-next-step">
          <div>
            <p className="font-heading text-lg font-bold text-white">Found your rate?</p>
            <p className="mt-1 text-sm text-slate-300">Create a free account to book your shipment, or request a doorstep pickup.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" data-testid="rate-book-cta"
                  className="rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover">
              Book a Shipment
            </Link>
            <Link to="/pickup-request" data-testid="rate-pickup-cta"
                  className="rounded-full border border-white/25 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10">
              Request Pickup
            </Link>
          </div>
        </div>
      </section>

      <Footer contact={contact} />
    </div>
  );
}
